import { sanitizeHtml } from "client/Misc/htmlUtils";
import { ensureNotNil, isNilOrWhiteSpace } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import {
	DOMParser,
	DOMSerializer,
	Fragment,
	Node,
	Schema,
	Slice
} from "prosemirror-model";
import {
	EditorState,
	Selection,
	Transaction
} from "prosemirror-state";
import { useCallback, useMemo } from "react";
import { RichTextEditorState, RichTextEditorStateKind } from "../types";
import { RewriteToneKind, TranslateLangKind } from "client/Api/AiAssistant/aiAssistant";
import {
	RewriteToolHandlers,
	RewriteToolResponse,
	RewriteToolsResult,
	SuccessfulRewriteToolResponse
} from "../../RewriteToolsMenu/common";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { useValueEqualMemo } from "client/Hooks";

const MAX_CONTENT_LENGTH = 20_000;

export type SuccessfulRichTextEditorRewriteToolResponse = SuccessfulRewriteToolResponse<Slice, EditorState>;
export type RichTextEditorRewriteToolResponse = RewriteToolResponse<Slice, EditorState>;

export type RichTextEditorRewriteToolHandlers = RewriteToolHandlers<Slice, EditorState>;

export type RichTextEditorRewriteToolsResult = RewriteToolsResult<Slice, EditorState>;

export type RichTextEditorRewriteToolsService = {
	makeShorter: (html: string, signal?: AbortSignal) => Promise<string>;
	makeLonger: (html: string, signal?: AbortSignal) => Promise<string>;
	fixSpelling: (html: string, signal?: AbortSignal) => Promise<string>;
	changeTone: (html: string, tone: RewriteToneKind, signal?: AbortSignal) => Promise<string>;
	translate: (html: string, lang: TranslateLangKind, signal?: AbortSignal) => Promise<string>;
};

export type RichTextEditorRewriteToolsRange =
	| {
		kind: "entire-document";
	}
	| {
		kind: "selection";
		shouldUseEntireDocumentWhenEmpty?: boolean;
	};

export function useRichTextEditorRewriteTools(
	state: RichTextEditorState,
	onStateChange: (newState: RichTextEditorState, isSignificant: boolean) => void,
	onFocusInput: (() => void) | "NONE",
	range: RichTextEditorRewriteToolsRange,
	service: RichTextEditorRewriteToolsService
): RichTextEditorRewriteToolsResult {
	if (state.kind !== RichTextEditorStateKind.ProseMirror) {
		throw new Error("This component only works with prosemirror state");
	}

	const {
		editorState
	} = state;

	const stableRange = useValueEqualMemo(
		(): RichTextEditorRewriteToolsRange => range,
		[range]
	);

	const [from, to] = useMemo(
		(): [number, number] => {
			const entireDocumentRange: [number, number] = [0, editorState.doc.nodeSize - 2];
			switch (stableRange.kind)
			{
				case "selection":
					const selection: Selection = editorState.selection;

					if (stableRange.shouldUseEntireDocumentWhenEmpty
						&& selection.empty
					) {
						return entireDocumentRange;
					}

					return [selection.from, selection.to];
				case "entire-document":
					return entireDocumentRange;
				default:
					never(stableRange);
			}
		},
		[
			editorState.doc.nodeSize,
			editorState.selection,
			stableRange
		]
	);

	const applyHandler = useCallback(
		(
			response: SuccessfulRichTextEditorRewriteToolResponse
		): void => {
			const {
				updatedSlice,
				originalSliceInfo: {
					entireContent: originalContent,
					from,
					to
				}
			} = response;

			if (!originalContent.doc.eq(editorState.doc)) {
				throw new Error("Document has changed since improvement was requested.");
			}

			let tr: Transaction;

			// NOTE: This code tries to insert Slice into its original place, if html structure of the slice
			// was not changed during the rewrite operation it should fit correctly in the original place
			// If structure did change, replace will throw an error and in that case we fallback to "replaceWith"
			// which is more loose variant of the "replace" method and will try to fit the new content as best as possible.
			try {
				tr = editorState.tr;

				tr.replace(
					from,
					to,
					updatedSlice
				);
			} catch(e) {
				tr = editorState.tr;

				tr.replaceWith(
					from,
					to,
					updatedSlice.content
				);
			}

			const updatedState: EditorState = editorState.apply(tr);

			onStateChange(
				{
					kind: RichTextEditorStateKind.ProseMirror,
					editorState: updatedState
				},
				true
			);

			if (onFocusInput !== "NONE") {
				onFocusInput();
			}
		},
		[
			editorState,
			onFocusInput,
			onStateChange
		]
	);

	return useMemo(
		(): RichTextEditorRewriteToolsResult => {
			const text: string = editorState.doc
				.textBetween(from, Math.min(to, from + 500));

			if (isNilOrWhiteSpace(text)) {
				return {
					canRewrite: false
				};
			}

			const handlers: RichTextEditorRewriteToolHandlers = {
				makeShorter: (signal) => getRewriteToolResponse(
					editorState,
					from,
					to,
					(html, signal) => service.makeShorter(html, signal),
					signal
				),
				makeLonger: (signal) => getRewriteToolResponse(
					editorState,
					from,
					to,
					(html, signal) => service.makeLonger(html, signal),
					signal
				),
				fixSpelling: (signal) => getRewriteToolResponse(
					editorState,
					from,
					to,
					(html, signal) => service.fixSpelling(html, signal),
					signal
				),
				changeTone: (tone, signal) => getRewriteToolResponse(
					editorState,
					from,
					to,
					(html, signal) => service.changeTone(html, tone, signal),
					signal
				),
				translate: (lang, signal) => getRewriteToolResponse(
					editorState,
					from,
					to,
					(html, signal) => service.translate(html, lang, signal),
					signal
				),
				apply: applyHandler,
				getPreviewHtml: generatePreviewHtml,
				maxContentLength: MAX_CONTENT_LENGTH
			};

			return {
				canRewrite: true,
				handlers
			};
		},
		[
			editorState,
			from,
			to,
			applyHandler,
			service
		]
	);
}

async function getRewriteToolResponse(
	editorState: EditorState,
	from: number,
	to: number,
	rewriteHtmlCallback: (originalHtml: string, signal?: AbortSignal) => Promise<string>,
	signal: AbortSignal | undefined
): Promise<RichTextEditorRewriteToolResponse> {
	const originalSlice: Slice = editorState.doc
		.slice(
			from,
			to,
			true
		);

	const {
		openStart,
		openEnd,
		content
	} = originalSlice;

	const extractedDataUrlMap = new Map<string, string>();

	const html: string = convertToHtml(
		content,
		editorState.schema,
		(doc) => extractDataUrl(doc, extractedDataUrlMap)
	);

	if (html.length > MAX_CONTENT_LENGTH) {
		return {
			status: "content-too-large",
			originalSliceInfo: {
				entireContent: editorState,
				from,
				to
			}
		};
	}

	const updatedHtml: string = await rewriteHtmlCallback(html, signal);

	const updatedContent: Fragment = convertToFragment(
		updatedHtml,
		editorState.schema,
		(doc) => reapplyDataUrl(doc, extractedDataUrlMap)
	);

	const updatedSlice: Slice = new Slice(
		updatedContent,
		openStart,
		openEnd
	);

	return {
		status: "success",
		originalSliceInfo: {
			entireContent: editorState,
			from,
			to
		},
		updatedSlice
	};
}

function convertToHtml(
	content: Fragment,
	schema: Schema<never, never>,
	preProcessDocument?: (doc: Document) => void
): string {
	const tempDocument: Document = document.implementation
		.createHTMLDocument();

	DOMSerializer.fromSchema(schema)
		.serializeFragment(
			content,
			{ document: tempDocument },
			tempDocument.body
		);

	preProcessDocument?.(tempDocument);

	return tempDocument.body
		.innerHTML;
}

function convertToFragment(
	html: string,
	schema: Schema<never, never>,
	preProcessDocument?: (doc: Document) => void
): Fragment {
	const sanitizedHtml: string = sanitizeHtml(
		html,
		{ globalStylesBehavior: "remove" }
	);

	const newDom: Document = document.implementation
		.createHTMLDocument();

	newDom.write(sanitizedHtml);
	newDom.close();

	preProcessDocument?.(newDom);

	const parsedNode: Node = DOMParser.fromSchema(schema)
		.parse(newDom);

	return parsedNode.content;
}

function generatePreviewHtml(response: SuccessfulRichTextEditorRewriteToolResponse): string {
	const {
		originalSliceInfo: {
			entireContent: originalContent
		},
		updatedSlice
	} = response;

	const tempDocument: Document = document.implementation
		.createHTMLDocument();

	DOMSerializer.fromSchema(originalContent.schema)
		.serializeFragment(
			updatedSlice.content,
			{ document: tempDocument },
			tempDocument.body
		);

	return tempDocument.body
		.innerHTML;
}

function extractDataUrl(doc: Document, resultMap: Map<string, string>): void {
	const images: NodeListOf<HTMLImageElement> = doc.querySelectorAll("img");

	for (const image of Array.from(images)) {
		if (!isLongDataUrl(image.src)) {
			continue;
		}

		const id: Guid = newGuid();
		resultMap.set(id, image.src);

		image.dataset["urlId"] = id;
		image.src = "";
	}

	const anchors: NodeListOf<HTMLAnchorElement> = doc.querySelectorAll("a");

	for (const anchor of Array.from(anchors)) {
		if (!isLongDataUrl(anchor.href)) {
			continue;
		}

		const id: Guid = newGuid();
		resultMap.set(id, anchor.href);

		anchor.dataset["urlId"] = id;
		anchor.href = "";
	}
}

function reapplyDataUrl(doc: Document, urlMap: Map<string, string>): void {
	const images: NodeListOf<Element> = doc.querySelectorAll("img[data-url-id]");

	for (const image of Array.from(images)) {
		if (!(image instanceof HTMLImageElement)) {
			continue;
		}

		const urlId: string | undefined = image.dataset["urlId"];

		if (!urlId || !urlMap.has(urlId)) {
			continue;
		}

		const url: string = ensureNotNil(urlMap.get(urlId));

		image.src = url;
		delete image.dataset["urlId"];
	}

	const anchors: NodeListOf<Element> = doc.querySelectorAll("a[data-url-id]");

	for (const anchor of Array.from(anchors)) {
		if (!(anchor instanceof HTMLAnchorElement)) {
			continue;
		}

		const urlId: string | undefined = anchor.dataset["urlId"];

		if (!urlId || !urlMap.has(urlId)) {
			continue;
		}

		const url: string = ensureNotNil(urlMap.get(urlId));

		anchor.href = url;
		delete anchor.dataset["urlId"];
	}
}

function isLongDataUrl(url: string): boolean {
	if (isNilOrWhiteSpace(url)) {
		return false;
	}

	const parsedUrl: URL | null = URL.parse(url);;

	if (!parsedUrl) {
		return false;
	}

	if (parsedUrl.protocol !== "data:") {
		return false;
	}

	if (url.length < 50) {
		return false;
	}

	return true;
}